import React, { FC, ReactElement, useContext, useEffect, useState } from 'react'
import { ConfigureAgents as ConfigureAgentsLanguage } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { isStringNotEmpty, isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { DEFAULT_CME_ID } from '../../AddUser/Constants'
import { getCMEIDFromTelSys, resolvePartitionInTelSys } from '../../Common/API/ItemGetters'
import { ALL_VALUE } from '../../Common/API/ItemsGetters'
import { ModalExitPromptContextProvider } from '../../Common/Components/Modal/ModalExitPromptContext'
import { StandardContexts } from '../../Common/Components/StandardContexts'
import { DocTopic } from '../../Common/Constants/Documentation'
import { ReactState, UserInfoContext, UserInfoContextProvider } from '../../Common/Contexts'
import { CommonFormContextProvider } from '../../Common/Contexts/CommonFormContext'
import { ModalContextProvider } from '../../Common/Contexts/ModalContext'
import { UserInfoContextType } from '../../Common/Contexts/UserInfoContext'
import { clearModalHelpTopic, setModalHelpTopicID } from '../../Common/Utils/HelpPortal'
import { ConfigureAgentsContextProvider } from '../Contexts/ConfigureAgentsContext'
import { ConfigureAgentsLoader } from '../Loader/ConfigureAgentsLoader'
import { ConfigureAgentsTabs } from '../Main/ConfigureAgentsTabs'
import { ConfigureAgentsAdapterInputs } from '../Types/Types'
import { Modal } from '../../../ReactRouting/Components/Molecules/Modal/Modal'
import { $window } from '../../../NonReact/common/AkixiWindow'

const ConfigureAgentsContainer: FC<ConfigureAgentsAdapterInputs> = ({
    selectedAgent,
    filters: { cmeID: filterCMEID, telSysID, partitionID: filterPartitionID, partIDInTelSys: filterPartIDInTelSys },
    viewOnly
}: ConfigureAgentsAdapterInputs): ReactElement => {
    const userInfo: UserInfoContextType = useContext(UserInfoContext)
    const [partitionID, setPartitionID]: ReactState<Nullable<number>> = useState<Nullable<number>>(null)
    const [cmeID, setCmeID]: ReactState<Nullable<string>> = useState<Nullable<string>>(null)

    useEffect(() => {
        let isActive: boolean = true

        getCMEIDFromTelSys(telSysID, filterCMEID).then((resolvedCmeID: Nullable<string>): void => {
            if (isActive) {
                setCmeID(resolvedCmeID)
            }
        })

        return (): void => {
            isActive = false
        }
    }, [filterCMEID, telSysID])

    useEffect(() => {
        if (isVarNotEmpty(filterPartIDInTelSys)) {
            setPartitionID(filterPartIDInTelSys)
            return undefined
        }

        let isActive: boolean = true

        resolvePartitionInTelSys(filterPartIDInTelSys, filterPartitionID).then(
            (resolvedPartitionID: Nullable<number>): void => {
                if (isActive) {
                    setPartitionID(resolvedPartitionID)
                }
            }
        )

        return (): void => {
            isActive = false
        }
    }, [filterPartIDInTelSys, filterPartitionID])

    return userInfo && partitionID !== null && cmeID !== null ? (
        <ConfigureAgentsContextProvider
            selectedAgent={selectedAgent}
            viewOnly={viewOnly ?? false}
            filters={{
                cmeID: cmeID ?? DEFAULT_CME_ID,
                telSysID: telSysID ?? ALL_VALUE,
                partitionID: partitionID ?? ALL_VALUE
            }}
        >
            <ConfigureAgentsTabs />
        </ConfigureAgentsContextProvider>
    ) : (
        <ConfigureAgentsLoader />
    )
}

export const ConfigureAgentsModal: FC<ConfigureAgentsAdapterInputs> = ({
    selectedAgent,
    filters,
    refreshAction,
    viewOnly,
    onModalExited
}: ConfigureAgentsAdapterInputs): ReactElement => {
    const language: CLanguageHandler = CLanguageHandler.getInstance()

    language.loadBundle(ConfigureAgentsLanguage.NAME)

    const [isModalOpen, setModalOpen] = useState<boolean>(true)

    const closeModal = (refresh?: boolean): void => {
        $window.disableHotKeys = false
        setModalOpen(false)

        if (refresh && refreshAction) {
            refreshAction()
        }
    }

    return (
        <div>
            <CommonFormContextProvider>
                <ModalExitPromptContextProvider>
                    <ModalContextProvider
                        modalInput={{
                            closeModal
                        }}
                    >
                        <Modal
                            isOpen={isModalOpen}
                            closeOnBackdropClick={true}
                            isClosableWithXButton={true}
                            inlineExitPromptSettings={{
                                message: language.get(ConfigureAgentsLanguage.ConfigureAgents_PromptMessage),
                                confirmButtonText: language.get(ConfigureAgentsLanguage.ConfigureAgents_PromptConfirm),
                                cancelButtonText: language.get(ConfigureAgentsLanguage.ConfigureAgents_PromptCancel),
                                isRouterMode: true
                            }}
                            onOpen={(): void => {
                                // Add contextual doc topic ID to the window object which is used
                                // in old JSP logic that handles F1 key press.
                                setModalHelpTopicID(DocTopic.ADMINACDAGENTSADD)
                            }}
                            onClose={closeModal}
                            onExited={(): void => {
                                clearModalHelpTopic()
                                onModalExited?.()
                            }}
                        >
                            {isModalOpen ? (
                                <StandardContexts>
                                    <UserInfoContextProvider>
                                        <div className='modal-title-report-config'>
                                            {isStringNotEmpty(selectedAgent)
                                                ? language.get(ConfigureAgentsLanguage.ConfigureAgents_Modify_Title)
                                                : language.get(ConfigureAgentsLanguage.ConfigureAgents_Add_Title)}
                                        </div>
                                        <div className='configure-agents'>
                                            <ConfigureAgentsContainer
                                                selectedAgent={selectedAgent}
                                                filters={filters}
                                                viewOnly={viewOnly}
                                            />
                                        </div>
                                    </UserInfoContextProvider>
                                </StandardContexts>
                            ) : (
                                <ConfigureAgentsLoader />
                            )}
                        </Modal>
                    </ModalContextProvider>
                </ModalExitPromptContextProvider>
            </CommonFormContextProvider>
        </div>
    )
}
